import { useContext } from "react";
import { createPortal } from "react-dom";
import SessionCountdown from "./SessionCountdown";
import SessionCountdownAnnouncement from "./SessionCountdownAnnouncement";
import { SessionCountdownContext } from "../state/SessionCountdownContext";
import { useSessionCountdownSlot } from "./sessionCountdownSlot";
import "./SessionCountdownBadge.css";

/**
 * GMA-1909 — the single countdown display for the whole app.
 *
 * Placed once in AppContent rather than in each shell. Two review rounds were
 * spent discovering surfaces a per-shell clock missed: WebShell (/web/*), then
 * /accept-terms and the standalone /call route, which render no shell at all
 * while AuthProvider still expires their session. One display cannot miss one,
 * and a surface added tomorrow inherits it.
 *
 * Reads the dedicated countdown context, not AuthContext: the value changes
 * every second, and AuthContext is consumed by large pages.
 */
const SessionCountdownBadge = () => {
  const { remainingMs, isWarning } = useContext(SessionCountdownContext) || {};
  const slot = useSessionCountdownSlot();
  const warning = Boolean(isWarning);
  const clock = <SessionCountdown remainingMs={remainingMs} isWarning={warning} />;

  // Reserved space wins. Floating only where no shell offers a slot
  // (/accept-terms, the standalone /call) - there is no header chrome to cover
  // there, which is exactly why the fallback is safe and the default is not.
  // The announcement is rendered here, outside the portal, and stays in this
  // position for the life of the app. The visual clock is what moves between
  // the slot and the floating fallback - remounting THAT is harmless.
  return (
    <>
      <SessionCountdownAnnouncement isWarning={warning} />
      {slot ? (
        createPortal(clock, slot)
      ) : (
        <div className="session-countdown-badge" data-testid="session-countdown-badge">
          {clock}
        </div>
      )}
    </>
  );
};

export default SessionCountdownBadge;
